import { request } from 'api/apiTS';
import { SegmentTag } from 'types/global';
import { ProductType } from 'types/Products';
import { LogEventType } from 'types/LogEvents';
import {
  baseApiLegacy,
  IS_UXBEAUTY,
  WHITELABEL_EVENT_KEY
} from 'components/constants/Constants';
import { useShouldLogEvents } from 'hooks/useUserState_utils';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Is product specific. We always have to pass a product ID.
 */

export const useLogProductPageOpenedEvent = () => {
  const logEvent = useLogEvent();

  const logProductPageOpenedEvent = useCallback(
    (
      productId: string | undefined,
      productName: string | undefined,
      segment: SegmentTag | undefined,
      supplierId: string | undefined,
      productType: ProductType | undefined,
      eventKey?: string
    ) => {
      if (!(productId && productName && segment && supplierId && productType))
        return;

      const eventType =
        productType === ProductType.INGREDIENTS
          ? LogEventType.PRODUCT_PERMALINK
          : productType === ProductType.FORMULATIONS
          ? LogEventType.FORMULATION_PERMALINK
          : LogEventType.SERVICE_PERMALINK;

      logEvent(
        [
          {
            productId,
            productName,
            segment,
            supplierId
          }
        ],
        eventType,
        eventKey
      );
    },
    [logEvent] // `logEvent` is stable, so it’s the only dependency
  );

  return logProductPageOpenedEvent;
};

export const useLogEvent = () => {
  const shouldLogEvent = useShouldLogEvents();

  // We use a ref to store `shouldLogEvent` because the value depends on the user's loading state and company type.
  // During loading, we default to `false` to avoid logging events for staging companies
  // When the user's state changes (e.g., from loading to authenticated), `shouldLogEvent` may update, but we want to avoid
  // unnecessary UI re-renders. By using a ref, we ensure the `logEvent` function always has the latest value without
  // triggering re-renders, maintaining both correctness and performance.
  const shouldLogEventRef = useRef(shouldLogEvent);

  useEffect(() => {
    shouldLogEventRef.current = shouldLogEvent;
  }, [shouldLogEvent]);

  const logEvent = useCallback(
    (
      eventInfo: {
        productId?: string;
        segment?: SegmentTag;
        supplierId?: string;
        [key: string]: any;
      }[],
      eventType: any,
      eventKey?: string
    ) => {
      if (!shouldLogEventRef.current) return;
      const key = IS_UXBEAUTY ? WHITELABEL_EVENT_KEY : eventKey;
      const body = {
        eventType: eventType,
        timestamp: Date.now(),
        eventInfo: eventInfo,
        ...(key && { eventKey: key })
      };

      const options = {
        method: 'POST',
        url: `${baseApiLegacy}/events/logEvent`,
        headers: { 'Content-Type': 'application/json' },
        data: body
      };

      request(
        options,
        () => {},
        () => {}
      );
    },
    []
  );

  return logEvent;
};
